/**
 * Media Store
 *
 * Centralised cache for media library items.
 * Items are fetched once (on first call to fetchMedia) and kept in memory
 * for the lifetime of the session — re-opening the Media panel or the
 * Media Picker modal is therefore a no-op fetch-wise.
 *
 * Call `refresh()` to force a re-fetch (e.g. after bulk operations).
 */

import { create } from 'zustand';
import { mediaService, type MediaItem } from '@/lib/api-services/media-service';

interface MediaState {
  // ── State ──────────────────────────────────────────────────────────────────
  items: MediaItem[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  hasFetched: boolean;

  // ── Actions ────────────────────────────────────────────────────────────────
  /** Fetch all media items. No-op if already fetched. */
  fetchMedia: () => Promise<void>;
  /** Upload one or more files and prepend them to the item list. */
  uploadMedia: (files: File[]) => Promise<void>;
  /** Delete a media item by its full URL. */
  deleteMedia: (item: MediaItem) => Promise<void>;
  /** Force a fresh fetch regardless of hasFetched. */
  refresh: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useMediaStore = create<MediaState>((set, get) => ({
  items: [],
  isLoading: false,
  isUploading: false,
  error: null,
  hasFetched: false,

  fetchMedia: async () => {
    const { hasFetched, isLoading } = get();
    if (hasFetched || isLoading) return;

    try {
      set({ isLoading: true, error: null });
      const items = await mediaService.getMedia();
      set({ items, isLoading: false, hasFetched: true });
    } catch (err) {
      console.error('Failed to load media:', err);
      set({ isLoading: false, error: 'Failed to load media items' });
    }
  },

  uploadMedia: async (files: File[]) => {
    try {
      set({ isUploading: true, error: null });
      const uploaded = await mediaService.uploadMultipleMedia(files);
      set((state) => ({
        items: [...uploaded, ...state.items],
        isUploading: false,
      }));
    } catch (err) {
      console.error('Failed to upload media:', err);
      set({ isUploading: false, error: 'Failed to upload files' });
    }
  },

  deleteMedia: async (item: MediaItem) => {
    try {
      set({ error: null });
      await mediaService.deleteMedia(item.path);
      set((state) => ({
        items: state.items.filter((i) => i.url !== item.url),
      }));
    } catch (err) {
      console.error('Failed to delete media:', err);
      set({ error: 'Failed to delete file' });
    }
  },

  refresh: async () => {
    set({ hasFetched: false });
    await get().fetchMedia();
  },

  clearError: () => set({ error: null }),

  reset: () => set({ items: [], isLoading: false, isUploading: false, error: null, hasFetched: false }),
}));
